import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { v4 as uuid } from 'uuid';
import {
  DEFAULT_SORT_PROFILE,
  type SortCriterion,
  type SortProfile,
} from '../types/sort-profile.types';
import { resolveSortProfile } from '../utils/resolve-sort-profile';

export type FavouriteHand = 'left' | 'right';
export type TextSize = 'medium' | 'large';

interface PreferencesStore {
  profiles: SortProfile[];
  favouriteHand: FavouriteHand;
  textSize: TextSize;
  getDefaultProfile: () => SortProfile;
  getProfileForCustomer: (customerId: string | null) => SortProfile;
  setFavouriteHand: (hand: FavouriteHand) => void;
  setTextSize: (size: TextSize) => void;
  saveDefaultProfile: (criteria: SortCriterion[]) => void;
  addProfile: (
    name: string,
    criteria: SortCriterion[],
    customerIds: string[],
  ) => void;
  updateProfile: (
    id: string,
    updates: Partial<Pick<SortProfile, 'name' | 'criteria' | 'customerIds'>>,
  ) => void;
  deleteProfile: (id: string) => void;
  assignProfileToCustomer: (profileId: string, customerId: string) => void;
  removeProfileFromCustomer: (customerId: string) => void;
}

export const usePreferencesStore = create<PreferencesStore>()(
  devtools(
    persist(
      immer((set, get) => ({
        profiles: [DEFAULT_SORT_PROFILE],
        favouriteHand: 'right',
        textSize: 'medium',

        setFavouriteHand: (hand) =>
          set((state) => {
            state.favouriteHand = hand;
          }),

        setTextSize: (size) =>
          set((state) => {
            state.textSize = size;
          }),

        getDefaultProfile: () =>
          get().profiles.find((p) => p.isDefault) ?? DEFAULT_SORT_PROFILE,

        getProfileForCustomer: (customerId) =>
          resolveSortProfile(get().profiles, customerId),

        saveDefaultProfile: (criteria) =>
          set((state) => {
            const idx = state.profiles.findIndex((p) => p.isDefault);
            if (idx !== -1) {
              state.profiles[idx].criteria = criteria;
              state.profiles[idx].updatedAt = new Date().toISOString();
            }
          }),

        addProfile: (name, criteria, customerIds) =>
          set((state) => {
            state.profiles.push({
              id: uuid(),
              name,
              criteria,
              customerIds,
              isDefault: false,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            });
          }),

        updateProfile: (id, updates) =>
          set((state) => {
            const idx = state.profiles.findIndex((p) => p.id === id);
            if (idx !== -1) {
              Object.assign(state.profiles[idx], updates);
              state.profiles[idx].updatedAt = new Date().toISOString();
            }
          }),

        deleteProfile: (id) =>
          set((state) => {
            state.profiles = state.profiles.filter(
              (p) => p.id !== id && !p.isDefault,
            );
          }),

        assignProfileToCustomer: (profileId, customerId) =>
          set((state) => {
            state.profiles.forEach((p) => {
              p.customerIds = p.customerIds.filter((cid) => cid !== customerId);
            });
            const idx = state.profiles.findIndex((p) => p.id === profileId);
            if (idx !== -1 && !state.profiles[idx].isDefault) {
              state.profiles[idx].customerIds.push(customerId);
              state.profiles[idx].updatedAt = new Date().toISOString();
            }
          }),

        removeProfileFromCustomer: (customerId) =>
          set((state) => {
            state.profiles.forEach((p) => {
              p.customerIds = p.customerIds.filter((cid) => cid !== customerId);
            });
          }),
      })),
      {
        name: 'agent-preferences',
        partialize: (state) => ({
          profiles: state.profiles,
          favouriteHand: state.favouriteHand,
          textSize: state.textSize,
        }),
      },
    ),
    { name: 'PreferencesStore' },
  ),
);
